
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { createTheme, CssBaseline, ThemeProvider } from "@mui/material";
import './index.css';
import './styles/responsive-modal-system.css';
import './styles/popup-system.css';
import 'leaflet/dist/leaflet.css';
import App from './App';
import { Provider } from "react-redux";

import L from 'leaflet';
import { store } from './store/store';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/assets/marker-icon-2x.png',
  iconUrl: '/assets/marker-icon.png',
  shadowUrl: '/assets/marker-shadow.png',
});

const theme = createTheme({
  palette: {
    primary: {
      main: "#1d63c8",
      light: "#78cfff",
      dark: "#114fae",
      contrastText: "#ffffff",
    },
  },
  typography: {
    h5: {
      fontSize: "clamp(1.25rem, 1.6vw, 1.9rem)",
      fontWeight: 800,
    },
    h6: {
      fontSize: "clamp(1rem, 1.2vw, 1.3rem)",
      fontWeight: 800,
    },
    body1: {
      fontSize: "clamp(0.875rem, 0.95vw, 1rem)",
    },
    body2: {
      fontSize: "clamp(0.8125rem, 0.9vw, 0.95rem)",
    },
  },
  components: {
    MuiDialog: {
      defaultProps: {
        fullWidth: true,
        maxWidth: "xl",
      },
      styleOverrides: {
        paper: {
          // width: "min(var(--rs-modal-width), calc(100vw - 1rem))",
          // maxWidth: "min(var(--rs-modal-width), calc(100vw - 1rem))",
          maxHeight: "var(--rs-modal-height)",
          borderRadius: "var(--rs-modal-radius)",
          overflow: "hidden",
          "@media (max-width: 767px)": {
            width: "100vw",
            maxWidth: "100vw",
            minHeight: "100dvh",
            maxHeight: "100dvh",
            borderRadius: 0,
            margin: 0,
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: "var(--rs-modal-pad) calc(var(--rs-modal-pad) + 0.25rem)",
          fontSize: "var(--rs-modal-title)",
          fontWeight: 800,
          lineHeight: 1.2,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "0 var(--rs-modal-pad) var(--rs-modal-pad)",
          overflow: "auto",
          "&.MuiDialogContent-dividers": {
            padding: "var(--rs-modal-pad)",
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "var(--rs-modal-pad-sm) var(--rs-modal-pad)",
          gap: "0.75rem",
          minHeight: "auto",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          "@media (max-width: 1536px), (max-height: 900px)": {
            "--compact-paper-density": 1,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: "var(--rs-modal-btn-h)",
          fontSize: "var(--rs-modal-body)",
          borderRadius: 10,
          textTransform: "none",
          fontWeight: 700,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "@media (max-width: 1536px), (max-height: 900px)": {
            padding: 7,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: "var(--rs-modal-control-h)",
          fontSize: "var(--rs-modal-body)",
          "@media (max-width: 1536px), (max-height: 900px)": {
            minHeight: "var(--rs-modal-control-h-compact)",
          },
        },
        input: {
          paddingTop: 10,
          paddingBottom: 10,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "var(--rs-modal-body)",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 42,
          fontSize: "var(--rs-modal-body)",
          paddingInline: 12,
          "@media (max-width: 1536px), (max-height: 900px)": {
            minHeight: 38,
            paddingInline: 10,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: "var(--rs-modal-table)",
          paddingTop: 12,
          paddingBottom: 12,
          "@media (max-width: 1536px), (max-height: 900px)": {
            paddingTop: 9,
            paddingBottom: 9,
          },
        },
        head: {
          fontWeight: 800,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          overflow: "auto",
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
      },
      styleOverrides: {
        tooltip: {
          background: "linear-gradient(135deg, #1d63c8 0%, #114fae 100%)",
          color: "#ffffff",
          border: "1px solid rgba(120, 207, 255, 0.28)",
          boxShadow: "0 10px 24px rgba(29, 99, 200, 0.22)",
          fontWeight: 700,
        },
        arrow: {
          color: "#114fae",
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <Provider store={store}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App />
    </ThemeProvider>
  </Provider>
);

// Keep service worker out of development so stale cached bundles do not mask routing fixes.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    if (process.env.NODE_ENV === "production") {
      navigator.serviceWorker.register("/service-worker.js").catch((err) => {
        console.error("Service Worker registration failed:", err);
      });
      return;
    }

    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => {
        registration.unregister().catch((error) => {
          console.warn("Service Worker unregister failed:", error);
        });
      });
    });
  });
}
